import React, { useState } from 'react';
import Turnstile from '@ajat/design-system/Turnstile';
import { ComponentPage, DemoCard } from '../shared';

/**
 * Cloudflare's published test keys — they need no account and never reach a
 * real challenge. Never use these in the app; the real key lives in
 * src/config/turnstile.js.
 */
const TEST_KEYS = {
  pass: '1x00000000000000000000AA',
  block: '2x00000000000000000000AB',
  interactive: '3x00000000000000000000FF',
};

const TokenReadout = ({ token }) => {
  if (token === null) return <span className="typo-body" style={{ color: 'var(--ds-doc-ink-muted)' }}>Waiting for a token…</span>;
  if (token === '') return <span className="typo-body-sm" style={{ color: 'var(--color-ui-error)' }}>Token cleared — challenge failed or expired</span>;
  return (
    <span className="typo-body-sm" style={{ color: 'var(--color-ui-success)', wordBreak: 'break-all' }}>
      Token received: <code>{token.slice(0, 32)}…</code>
    </span>
  );
};

export const meta = {
  component: Turnstile,
  componentName: 'Turnstile',
  title: 'Turnstile',
  group: 'Forms',
  importPath: '@ajat/design-system/Turnstile',
  description:
    'Cloudflare Turnstile in managed mode. It loads the Cloudflare script once per page, renders the widget explicitly and reports the verification token through onToken — an empty string when the challenge fails or expires, so a stale token can be invalidated. It fails open: if the script never loads no token is produced, and the backend still has its honeypot and rate limit.',
  props: [
    ['siteKey', 'string', '—', 'Cloudflare site key — the app reads it from src/config/turnstile.js'],
    ['onToken', 'func', '—', "Fires with the token, or '' when the challenge fails or expires"],
    ['className', 'string', '—', 'Additional CSS class — Contact reserves the widget height with one'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand.',
  usage: [
    {
      title: 'On the contact form',
      code: `import Turnstile from '@ajat/design-system/Turnstile';
import { TURNSTILE_SITE_KEY } from '../../../config/turnstile';

<Turnstile
  siteKey={TURNSTILE_SITE_KEY}
  onToken={(token) => contact.setField('captchaToken', token)}
  className="contact-page__turnstile"
/>

// The token is posted as \`captchaToken\` and verified server-side.
// Captcha is only ENFORCED where a secret is configured, so a
// missing token never blocks a legitimate submission on its own.`,
    },
    {
      title: 'Test keys',
      code: `// Cloudflare's published test keys — no account needed.
// Never ship these; the real key is in src/config/turnstile.js.
'1x00000000000000000000AA'   // always passes
'2x00000000000000000000AB'   // always blocks
'3x00000000000000000000FF'   // forces an interactive challenge`,
    },
  ],
};

export default function TurnstilePage() {
  const [pass, setPass] = useState(null);
  const [block, setBlock] = useState(null);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Always passes" hint="Cloudflare test key 1x0000…AA">
        <div className="flex flex-col gap-[var(--space-ui-medium-s)]">
          <Turnstile siteKey={TEST_KEYS.pass} onToken={setPass} />
          <TokenReadout token={pass} />
        </div>
      </DemoCard>

      <DemoCard title="Always blocks" hint="Cloudflare test key 2x0000…AB">
        <div className="flex flex-col gap-[var(--space-ui-medium-s)]">
          <Turnstile siteKey={TEST_KEYS.block} onToken={setBlock} />
          <TokenReadout token={block} />
        </div>
      </DemoCard>

      <DemoCard title="Fails open" surface={false}>
        <p className="typo-body-sm" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch' }}>
          If the Cloudflare script cannot load — offline, blocked by an extension, or a network error —
          the widget simply never produces a token and the form still submits. The backend&apos;s honeypot
          and rate limit are what actually hold the line, and the captcha is only enforced server-side
          where a secret is configured.
        </p>
      </DemoCard>
    </ComponentPage>
  );
}
